import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { categories } from "@/mocks/categories";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleCategoryClick = (url: string) => {
    window.open(url, "_blank", "noopener,nofollow");
    setMenuOpen(false);
  };

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled
            ? "bg-white/95 backdrop-blur-md shadow-sm"
            : "bg-black/30 backdrop-blur-sm"
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 md:h-20 flex items-center justify-between">
          {/* Logo */}
          <Link
            to="/"
            className="font-heading font-bold text-lg md:text-xl tracking-wide cursor-pointer"
          >
            <span className={scrolled ? "text-foreground-950" : "text-white"}>
              GTBUY
            </span>
            <span className={scrolled ? "text-primary-500" : "text-white/80"}>
              {" "}
              SHEET
            </span>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-6">
            <Link
              to="/"
              className={`font-label text-sm font-medium cursor-pointer transition-colors ${
                scrolled
                  ? "text-foreground-600 hover:text-foreground-950"
                  : "text-white/70 hover:text-white"
              }`}
            >
              Home
            </Link>
            <Link
              to="/blog/gtbuy-spreadsheet-shopping-guide"
              className={`font-label text-sm font-medium cursor-pointer transition-colors ${
                scrolled
                  ? "text-foreground-600 hover:text-foreground-950"
                  : "text-white/70 hover:text-white"
              }`}
            >
              Blog
            </Link>
            <div
              className={`h-4 w-px ${
                scrolled ? "bg-foreground-200" : "bg-white/20"
              }`}
            ></div>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="whitespace-nowrap px-5 py-2 bg-white text-foreground-950 font-label font-semibold text-sm rounded-full hover:bg-white/90 transition-all cursor-pointer"
            >
              gtbuy sheet
            </a>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="whitespace-nowrap px-5 py-2 bg-primary-500 text-white font-label font-semibold text-sm rounded-full hover:bg-primary-600 transition-all cursor-pointer"
            >
              buy it now
            </a>
          </div>

          {/* Mobile Hamburger */}
          <button
            onClick={() => setMenuOpen(!menuOpen)}
            className={`md:hidden w-10 h-10 flex items-center justify-center rounded-full cursor-pointer transition-colors ${
              scrolled
                ? "text-foreground-950 hover:bg-background-100"
                : "text-white hover:bg-white/10"
            }`}
            aria-label="Toggle menu"
          >
            {menuOpen ? (
              <i className="ri-close-line text-2xl"></i>
            ) : (
              <i className="ri-menu-line text-2xl"></i>
            )}
          </button>
        </div>
      </nav>

      {/* Mobile Menu Panel */}
      <div
        className={`fixed inset-0 z-40 md:hidden transition-all duration-300 ${
          menuOpen ? "visible" : "invisible"
        }`}
      >
        {/* Backdrop */}
        <div
          className={`absolute inset-0 bg-black/40 backdrop-blur-sm transition-opacity duration-300 ${
            menuOpen ? "opacity-100" : "opacity-0"
          }`}
          onClick={() => setMenuOpen(false)}
        ></div>

        {/* Panel */}
        <div
          className={`absolute top-0 right-0 w-full max-w-sm h-full bg-white shadow-2xl transition-transform duration-300 ${
            menuOpen ? "translate-x-0" : "translate-x-full"
          }`}
        >
          <div className="h-full overflow-y-auto p-6">
            {/* Header */}
            <div className="flex items-center justify-between mb-8">
              <span className="font-heading font-bold text-lg tracking-wide">
                <span className="text-foreground-950">GTBUY</span>
                <span className="text-primary-500"> SHEET</span>
              </span>
              <button
                onClick={() => setMenuOpen(false)}
                className="w-10 h-10 flex items-center justify-center rounded-full text-foreground-600 hover:bg-background-100 cursor-pointer transition-colors"
                aria-label="Close menu"
              >
                <i className="ri-close-line text-2xl"></i>
              </button>
            </div>

            {/* Quick Links */}
            <div className="mb-6">
              <p className="text-xs uppercase tracking-widest text-foreground-400 font-label mb-3">
                Navigation
              </p>
              <Link
                to="/"
                onClick={() => setMenuOpen(false)}
                className="flex items-center justify-between py-3 border-b border-background-100 cursor-pointer"
              >
                <span className="font-label font-medium text-foreground-950">
                  Home
                </span>
                <div className="w-6 h-6 flex items-center justify-center">
                  <i className="ri-arrow-right-s-line text-foreground-400"></i>
                </div>
              </Link>
              <Link
                to="/blog/gtbuy-spreadsheet-shopping-guide"
                onClick={() => setMenuOpen(false)}
                className="flex items-center justify-between py-3 border-b border-background-100 cursor-pointer"
              >
                <span className="font-label font-medium text-foreground-950">
                  Blog Guide
                </span>
                <div className="w-6 h-6 flex items-center justify-center">
                  <i className="ri-arrow-right-s-line text-foreground-400"></i>
                </div>
              </Link>
            </div>

            {/* Categories */}
            <div className="mb-6">
              <p className="text-xs uppercase tracking-widest text-foreground-400 font-label mb-3">
                Shop by Category
              </p>
              <div className="space-y-0.5">
                {categories.map((cat) => (
                  <button
                    key={cat.seq}
                    onClick={() => handleCategoryClick(cat.url)}
                    className="w-full flex items-center justify-between py-3 px-3 rounded-lg hover:bg-background-50 transition-colors cursor-pointer text-left"
                  >
                    <span className="font-label text-sm text-foreground-800">
                      {cat.name}
                    </span>
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-external-link-line text-foreground-400 text-xs"></i>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col gap-3 pt-2">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="w-full whitespace-nowrap px-5 py-3 bg-foreground-950 text-white font-label font-semibold text-sm rounded-full text-center cursor-pointer"
              >
                gtbuy sheet
              </a>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="w-full whitespace-nowrap px-5 py-3 bg-primary-500 text-white font-label font-semibold text-sm rounded-full text-center cursor-pointer"
              >
                buy it now
              </a>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}